import { useId } from "react";

export type ModelReleaseEvidenceEntry = {
  date: string;
  label: string;
  status: "Confirmed" | "Reported" | "Speculative";
  url?: string;
};

export type ModelReleaseEvidenceVisualsProps = {
  variant: "evidence" | "cost" | "scorecard";
  evidence?: ModelReleaseEvidenceEntry[];
};

const EVIDENCE_LEVELS = {
  Confirmed: {
    description: "Official announcement or documentation. Check exactly what it confirms.",
    sourceLabel: "Official source",
  },
  Reported: {
    description: "Third-party observation or report, not an official launch announcement.",
    sourceLabel: "Reported source",
  },
  Speculative: {
    description: "An unconfirmed interpretation, expectation or prediction.",
    sourceLabel: "Speculation source",
  },
} as const;

const COST_STEPS = [
  {
    label: "Model usage",
    explanation: "Record input, output and reasoning usage, with applicable caching and pricing.",
  },
  {
    label: "Tool calls",
    explanation: "Include paid APIs, retrieval and infrastructure used by the workflow.",
  },
  {
    label: "Retries",
    explanation: "Count failed attempts and rework; do not count the same usage twice.",
  },
  {
    label: "Human verification",
    explanation: "Include review time and corrections needed to accept the work.",
  },
  {
    label: "Successful business outcome",
    explanation: "Count only tasks that meet a predefined acceptance standard.",
  },
] as const;

const SCORECARD_CRITERIA = [
  {
    label: "Task accuracy",
    explanation: "Check factual correctness and source support against a fixed rubric.",
  },
  {
    label: "Completion rate",
    explanation: "Record accepted tasks out of all attempted tasks, including failures.",
  },
  {
    label: "Latency",
    explanation: "Measure end-to-end time to an accepted result, including retries and review.",
  },
  {
    label: "Cost",
    explanation: "Measure total workflow cost per accepted task over the same test period.",
  },
  {
    label: "Human intervention",
    explanation: "Record reviewer time, corrections and approval steps required per task.",
  },
  {
    label: "Reliability",
    explanation: "Repeat the same task set; record consistency, tool failures and error recovery.",
  },
] as const;

const FIGURE_COPY = {
  evidence: {
    eyebrow: "Evidence, not launch hype",
    title: "Model Evidence Tracker",
    introduction: "Read each reference with its evidence level. A reported identifier is not a confirmed public release.",
  },
  cost: {
    eyebrow: "Measure the whole workflow",
    title: "The Real Cost of an AI Agent",
    introduction: "A lower token price alone does not establish a lower cost per completed task.",
  },
  scorecard: {
    eyebrow: "Evaluate before switching",
    title: "AI Model Evaluation Scorecard",
    introduction: "An unfilled assessment checklist for your own task set, not a vendor comparison or benchmark.",
  },
} as const;

// Local selectors keep this extension independent of shared article styles.
const MODEL_RELEASE_VISUAL_CSS = `
  .model-release-visual {
    --mrv-teal: #00D4AA;
    --mrv-amber: #FF8C00;
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    margin: 2rem 0;
    border: 1px solid rgba(255, 255, 255, .14);
    border-radius: 1rem;
    background: #0d1f2d;
    color: #f1f5f9;
    font-family: var(--font-sans);
    text-align: left;
    overflow-wrap: anywhere;
  }
  .model-release-visual * { box-sizing: border-box; min-width: 0; }
  .model-release-visual .mrv-header,
  .model-release-visual .mrv-body,
  .model-release-visual .mrv-caption { padding: 1.25rem; }
  .model-release-visual .mrv-header { border-bottom: 1px solid rgba(255, 255, 255, .1); }
  .model-release-visual .mrv-eyebrow {
    margin: 0;
    color: var(--mrv-teal);
    font-size: .75rem;
    font-weight: 600;
    letter-spacing: .16em;
    text-transform: uppercase;
  }
  .model-release-visual h3 {
    margin: .5rem 0 0;
    color: #fff;
    font-family: var(--font-display);
    font-size: clamp(1.25rem, 3.5vw, 1.5rem);
    font-weight: 400;
    line-height: 1.25;
    letter-spacing: -.02em;
    text-wrap: pretty;
  }
  .model-release-visual .mrv-intro,
  .model-release-visual .mrv-description,
  .model-release-visual .mrv-caption,
  .model-release-visual .mrv-empty {
    margin: .5rem 0 0;
    color: #cbd5e1;
    font-size: .875rem;
    line-height: 1.65;
    text-wrap: pretty;
  }
  .model-release-visual .mrv-list {
    display: grid;
    gap: 1rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .model-release-visual .mrv-legend {
    display: grid;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
  .model-release-visual .mrv-status {
    display: inline-flex;
    max-width: 100%;
    border: 1px solid currentColor;
    border-radius: 999px;
    padding: .2rem .65rem;
    font-size: .75rem;
    font-weight: 600;
    line-height: 1.5;
  }
  .model-release-visual [data-status="Confirmed"] { color: var(--mrv-teal); }
  .model-release-visual [data-status="Reported"] { color: var(--mrv-amber); border-style: dashed; }
  .model-release-visual [data-status="Speculative"] { color: #cbd5e1; border-style: dotted; }
  .model-release-visual .mrv-timeline { position: relative; }
  .model-release-visual .mrv-event {
    position: relative;
    margin-left: .35rem;
    padding: 0 0 1.25rem 1.5rem;
    border-left: 1px solid rgba(255, 255, 255, .25);
  }
  .model-release-visual .mrv-event:last-child { padding-bottom: 0; }
  .model-release-visual .mrv-event::before {
    content: "";
    position: absolute;
    top: .3rem;
    left: -.35rem;
    width: .65rem;
    height: .65rem;
    border: 2px solid #cbd5e1;
    border-radius: 50%;
    background: #0d1f2d;
  }
  .model-release-visual .mrv-event[data-status="Confirmed"]::before { border-color: var(--mrv-teal); background: var(--mrv-teal); }
  .model-release-visual .mrv-event[data-status="Reported"]::before { border-color: var(--mrv-amber); border-radius: 0; }
  .model-release-visual .mrv-date { margin: 0 0 .5rem; color: #cbd5e1; font-size: .8125rem; line-height: 1.5; }
  .model-release-visual .mrv-label { margin: .5rem 0 0; color: #f1f5f9; font-size: 1rem; font-weight: 600; line-height: 1.5; }
  .model-release-visual .mrv-source { margin: .5rem 0 0; color: #cbd5e1; font-size: .875rem; line-height: 1.5; }
  .model-release-visual a { display: inline-block; padding-block: .25rem; color: var(--mrv-teal); text-decoration: underline; text-underline-offset: .2em; }
  .model-release-visual a:hover { color: #fff; }
  .model-release-visual a:focus-visible { outline: 2px solid var(--mrv-teal); outline-offset: 4px; border-radius: 2px; }
  .model-release-visual .mrv-step {
    position: relative;
    border: 1px solid rgba(255, 255, 255, .14);
    border-radius: .75rem;
    background: rgba(255, 255, 255, .025);
    padding: 1rem;
  }
  .model-release-visual .mrv-step:last-child { border-color: var(--mrv-teal); }
  .model-release-visual .mrv-step:not(:last-child)::after {
    content: "";
    position: absolute;
    bottom: -.75rem;
    left: 1.25rem;
    width: .45rem;
    height: .45rem;
    border-right: 1px solid var(--mrv-amber);
    border-bottom: 1px solid var(--mrv-amber);
    transform: rotate(45deg);
  }
  .model-release-visual .mrv-step-number { color: var(--mrv-amber); font-family: var(--font-mono); font-size: .75rem; }
  .model-release-visual .mrv-formula { margin-top: 1.5rem; padding: 1rem; border: 1px solid rgba(0, 212, 170, .3); border-radius: .75rem; }
  .model-release-visual .mrv-formula-title { margin: 0; color: var(--mrv-teal); font-size: 1rem; font-weight: 600; line-height: 1.5; }
  .model-release-visual .mrv-assessment { display: grid; gap: 0; margin: 0; }
  .model-release-visual .mrv-criterion { padding: 1rem 0; border-bottom: 1px solid rgba(255, 255, 255, .12); }
  .model-release-visual .mrv-criterion:first-child { padding-top: 0; }
  .model-release-visual .mrv-criterion:last-child { padding-bottom: 0; border-bottom: 0; }
  .model-release-visual dt { color: #f1f5f9; font-size: 1rem; font-weight: 600; line-height: 1.5; }
  .model-release-visual dd { margin: .5rem 0 0; }
  .model-release-visual .mrv-not-tested { color: var(--mrv-amber); font-family: var(--font-mono); font-size: .8125rem; line-height: 1.5; }
  .model-release-visual .mrv-caption { margin: 0; border-top: 1px solid rgba(255, 255, 255, .1); }
  @media (min-width: 640px) {
    .model-release-visual .mrv-header,
    .model-release-visual .mrv-body,
    .model-release-visual .mrv-caption { padding: 1.5rem 1.75rem; }
    .model-release-visual .mrv-legend { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    .model-release-visual .mrv-assessment { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem 1.5rem; }
    .model-release-visual .mrv-criterion { padding: 0 0 1rem; }
    .model-release-visual .mrv-criterion:nth-last-child(-n+2) { padding-bottom: 0; border-bottom: 0; }
  }
  @keyframes model-release-visual-enter {
    from { opacity: .88; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @media (prefers-reduced-motion: no-preference) {
    .model-release-visual .mrv-body { animation: model-release-visual-enter 480ms ease-out 1; }
  }
  @media (prefers-reduced-motion: reduce) {
    .model-release-visual .mrv-body { animation: none; transform: none; opacity: 1; }
  }
`;

// Evidence URLs are editorial input, but never allow executable link protocols.
function sourceHref(url?: string): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:" ? parsed.href : undefined;
  } catch {
    return undefined;
  }
}

function EvidenceTimeline({ evidence }: { evidence: ModelReleaseEvidenceEntry[] }) {
  return (
    <>
      <div className="mrv-legend" aria-label="Evidence levels">
        {Object.entries(EVIDENCE_LEVELS).map(([status, level]) => (
          <div key={status}>
            <span className="mrv-status" data-status={status}>{status}</span>
            <p className="mrv-description">{level.description}</p>
          </div>
        ))}
      </div>
      {evidence.length === 0 ? (
        <p className="mrv-empty">No evidence entries supplied. This tracker makes no release-status claims without dated, sourced entries.</p>
      ) : (
        <ol className="mrv-list mrv-timeline" aria-label="Evidence timeline in supplied editorial order">
          {evidence.map((entry, index) => {
            const href = sourceHref(entry.url);
            const sourceLabel = EVIDENCE_LEVELS[entry.status].sourceLabel;
            return (
              <li className="mrv-event" data-status={entry.status} key={`${index}-${entry.date}-${entry.label}`}>
                <p className="mrv-date">{entry.date}</p>
                <span className="mrv-status" data-status={entry.status}>{entry.status}</span>
                <p className="mrv-label">{entry.label}</p>
                <p className="mrv-source">
                  {href ? <a href={href} aria-label={`${sourceLabel}: ${entry.label}`}>{sourceLabel}</a> : "Source link not supplied or unavailable"}
                </p>
              </li>
            );
          })}
        </ol>
      )}
    </>
  );
}

function CostFlow() {
  return (
    <>
      <ol className="mrv-list" aria-label="Workflow cost and acceptance steps">
        {COST_STEPS.map((step, index) => (
          <li className="mrv-step" key={step.label}>
            <span className="mrv-step-number" aria-hidden="true">{index + 1}</span>
            <p className="mrv-label">{step.label}</p>
            <p className="mrv-description">{step.explanation}</p>
          </li>
        ))}
      </ol>
      <div className="mrv-formula">
        <p className="mrv-formula-title">Cost per accepted task</p>
        <p className="mrv-description">Total workflow cost across all attempts ÷ number of accepted tasks in the same period.</p>
        <p className="mrv-description">Include failed attempts in the numerator. The denominator is accepted tasks, not tokens, tool calls or attempted tasks.</p>
        <p className="mrv-description"><strong>Zero accepted tasks: uncomputed.</strong> Do not divide by zero or report a zero cost per accepted task.</p>
      </div>
    </>
  );
}

function EvaluationScorecard() {
  return (
    <dl className="mrv-assessment" aria-label="Six assessment criteria, all not tested">
      {SCORECARD_CRITERIA.map((criterion) => (
        <div className="mrv-criterion" key={criterion.label}>
          <dt>{criterion.label}</dt>
          <dd>
            <span className="mrv-not-tested">Not tested</span>
            <p className="mrv-description">{criterion.explanation}</p>
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Native text is the visual and its complete accessible static alternative.
 * Nothing waits for hydration, animation, a network request or user interaction.
 * Evidence dates, order, labels and classifications belong to the parent editor.
 */
export function ModelReleaseEvidenceVisuals({ variant, evidence = [] }: ModelReleaseEvidenceVisualsProps) {
  const id = useId();
  const titleId = `${id}-model-release-visual-title`;
  const captionId = `${id}-model-release-visual-caption`;
  const copy = FIGURE_COPY[variant];

  return (
    <figure className="not-prose model-release-visual" data-model-release-figure={variant} aria-labelledby={titleId} aria-describedby={captionId}>
      <style>{MODEL_RELEASE_VISUAL_CSS}</style>
      <div className="mrv-header">
        <p className="mrv-eyebrow">{copy.eyebrow}</p>
        <h3 id={titleId}>{copy.title}</h3>
        <p className="mrv-intro">{copy.introduction}</p>
      </div>
      <div className="mrv-body">
        {variant === "evidence" && <EvidenceTimeline evidence={evidence} />}
        {variant === "cost" && <CostFlow />}
        {variant === "scorecard" && <EvaluationScorecard />}
      </div>
      <figcaption className="mrv-caption" id={captionId}>
        {variant === "evidence" && "Evidence levels describe the supplied sources, not product availability. Official documentation may confirm a reference without confirming a public launch; reported and speculative references remain distinct from official announcements."}
        {variant === "cost" && "This is a measurement framework, not a savings estimate. Token pricing covers model usage only; retries, tools, infrastructure and human review also affect total workflow cost. The final outcome is the acceptance gate, not another cost item."}
        {variant === "scorecard" && "Use the same task set, acceptance rules, permissions and test period for each model. All six fields are unassessed: no vendor performance, sample results or winner is implied."}
      </figcaption>
    </figure>
  );
}

export default ModelReleaseEvidenceVisuals;
